import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tooltip,
  InputAdornment,
  Alert,
  Snackbar,
  Autocomplete,
  Grid,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Message,
  Keyboard,
  History,
} from '@mui/icons-material';
import { cannedResponseApi } from '../services/cannedResponseApi';
import { CannedResponse, CannedResponseCategory } from '../types/cannedResponse';
import './CannedResponseManagement.css';

const CannedResponseManagement: React.FC = () => {
  const [responses, setResponses] = useState<CannedResponse[]>([]);
  const [categories, setCategories] = useState<CannedResponseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [showInactive, setShowInactive] = useState(false);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingResponse, setEditingResponse] = useState<CannedResponse | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [responseToDelete, setResponseToDelete] = useState<CannedResponse | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    titleAr: '',
    content: '',
    contentAr: '',
    category: '',
    tags: [] as string[],
    shortcut: '',
    shortcutAr: '',
    isActive: true,
  });
  const [newCategory, setNewCategory] = useState('');

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [responsesData, categoriesData] = await Promise.all([
        cannedResponseApi.getAll(),
        cannedResponseApi.getCategories(),
      ]);
      setResponses(responsesData);
      setCategories(categoriesData);
    } catch (error) {
      showSnackbar('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const filteredResponses = responses.filter(response => {
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      !searchTerm ||
      response.title.toLowerCase().includes(q) ||
      (response.titleAr && response.titleAr.toLowerCase().includes(q)) ||
      response.titleAr?.includes(searchTerm) ||
      response.content.toLowerCase().includes(q) ||
      (response.contentAr && response.contentAr.toLowerCase().includes(q)) ||
      response.contentAr?.includes(searchTerm) ||
      response.shortcut?.toLowerCase().includes(q) ||
      response.shortcutAr?.toLowerCase().includes(q) ||
      response.tags.some(tag => tag.toLowerCase().includes(q));
    
    const matchesCategory = !selectedCategory || response.category === selectedCategory;
    const matchesActive = showInactive || response.isActive;
    
    return matchesSearch && matchesCategory && matchesActive;
  });

  const handleOpenModal = (response?: CannedResponse) => {
    if (response) {
      setEditingResponse(response);
      setFormData({
        title: response.title,
        titleAr: response.titleAr || '',
        content: response.content,
        contentAr: response.contentAr || '',
        category: response.category,
        tags: response.tags,
        shortcut: response.shortcut || '',
        shortcutAr: response.shortcutAr || '',
        isActive: response.isActive,
      });
    } else {
      setEditingResponse(null);
      setFormData({
        title: '',
        titleAr: '',
        content: '',
        contentAr: '',
        category: categories[0]?.id || '',
        tags: [],
        shortcut: '',
        shortcutAr: '',
        isActive: true,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingResponse(null);
    setNewCategory('');
  };

  const handleSave = async () => {
    try {
      if (editingResponse) {
        await cannedResponseApi.update(editingResponse.id, formData);
        showSnackbar('Response updated successfully', 'success');
      } else {
        await cannedResponseApi.create(formData);
        showSnackbar('Response created successfully', 'success');
      }
      handleCloseModal();
      loadData();
    } catch (error) {
      showSnackbar('Failed to save response', 'error');
    }
  };

  const handleDeleteClick = (response: CannedResponse) => {
    setResponseToDelete(response);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (responseToDelete) {
      try {
        await cannedResponseApi.delete(responseToDelete.id);
        showSnackbar('Response deleted successfully', 'success');
        loadData();
      } catch (error) {
        showSnackbar('Failed to delete response', 'error');
      }
    }
    setIsDeleteDialogOpen(false);
    setResponseToDelete(null);
  };

  const handleAddCategory = async () => {
    if (newCategory && !categories.find(c => c.name.toLowerCase() === newCategory.toLowerCase())) {
      try {
        const category = await cannedResponseApi.createCategory(newCategory);
        setCategories([...categories, category]);
        setFormData({ ...formData, category: category.id });
        setNewCategory('');
      } catch (error) {
        showSnackbar('Failed to create category', 'error');
      }
    }
  };

  const getCategoryColor = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.color || '#666';
  };

  const getCategoryName = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || categoryId;
  };

  const isFormValid = formData.title && formData.content && formData.category;

  return (
    <Box className="canned-response-management">
      {/* Header */}
      <Box className="crm-header">
        <Typography variant="h5" fontWeight="bold" sx={{ color: 'var(--text-primary)' }}>
          Canned Responses
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenModal()}
          sx={{
            backgroundColor: 'var(--primary-600)',
            '&:hover': { backgroundColor: 'var(--primary-700)' },
          }}
        >
          New Response
        </Button>
      </Box>

      {/* Filters */}
      <Paper className="crm-filters" sx={{ backgroundColor: 'var(--bg-elevated)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              placeholder="Search responses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'var(--text-muted)' }} />
                  </InputAdornment>
                ),
              }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'var(--bg-primary)',
                  '& fieldset': { borderColor: 'var(--border-light)' },
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                label="Category"
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map(cat => (
                  <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
              }
              label="Show inactive"
              sx={{ color: 'var(--text-secondary)' }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)', textAlign: 'right' }}>
              {filteredResponses.length} response{filteredResponses.length !== 1 ? 's' : ''}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} sx={{ backgroundColor: 'var(--bg-elevated)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'var(--bg-secondary)' }}>
              <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>Response</TableCell>
              <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>Category</TableCell>
              <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>Tags</TableCell>
              <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>Shortcuts</TableCell>
              <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>Usage</TableCell>
              <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ color: 'var(--text-secondary)' }}>
                  Loading...
                </TableCell>
              </TableRow>
            ) : filteredResponses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ color: 'var(--text-secondary)', py: 4 }}>
                  <Message sx={{ fontSize: 48, color: 'var(--text-muted)', mb: 1 }} />
                  <Typography>No responses found</Typography>
                  <Typography variant="body2" sx={{ color: 'var(--text-muted)' }}>
                    Create your first canned response to get started
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredResponses.map((response) => (
                <TableRow
                  key={response.id}
                  sx={{
                    opacity: response.isActive ? 1 : 0.5,
                    '&:hover': { backgroundColor: 'var(--bg-hover)' },
                  }}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={600} sx={{ color: 'var(--text-primary)' }}>
                        {response.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'var(--text-secondary)',
                          fontSize: '12px',
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {response.content}
                      </Typography>
                      {response.contentAr && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'var(--text-muted)',
                            fontSize: '11px',
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            direction: 'rtl',
                            textAlign: 'right',
                            mt: 0.25,
                          }}
                        >
                          {response.contentAr}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getCategoryName(response.category)}
                      size="small"
                      sx={{
                        backgroundColor: getCategoryColor(response.category) + '20',
                        color: getCategoryColor(response.category),
                        fontWeight: 500,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {response.tags.slice(0, 3).map(tag => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 20,
                            fontSize: '10px',
                            borderColor: 'var(--border-light)',
                            color: 'var(--text-secondary)',
                          }}
                        />
                      ))}
                      {response.tags.length > 3 && (
                        <Chip
                          label={`+${response.tags.length - 3}`}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '10px',
                            backgroundColor: 'var(--bg-tertiary)',
                            color: 'var(--text-muted)',
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                      {response.shortcut ? (
                        <Chip
                          label={response.shortcut}
                          size="small"
                          icon={<Keyboard sx={{ fontSize: 14 }} />}
                          sx={{
                            fontFamily: 'var(--font-mono)',
                            backgroundColor: 'var(--bg-tertiary)',
                            color: 'var(--text-secondary)',
                          }}
                        />
                      ) : null}
                      {response.shortcutAr ? (
                        <Chip
                          label={response.shortcutAr}
                          size="small"
                          sx={{
                            fontFamily: 'var(--font-mono)',
                            backgroundColor: 'var(--bg-secondary)',
                            color: 'var(--text-muted)',
                            fontSize: '10px',
                          }}
                        />
                      ) : null}
                      {!response.shortcut && !response.shortcutAr && (
                        <Typography variant="body2" sx={{ color: 'var(--text-muted)' }}>
                          -
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'var(--text-secondary)' }}>
                      <History sx={{ fontSize: 16 }} />
                      {response.usageCount}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={response.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={response.isActive ? 'success' : 'default'}
                      sx={{
                        backgroundColor: response.isActive ? 'var(--success-100)' : 'var(--bg-tertiary)',
                        color: response.isActive ? 'var(--success-700)' : 'var(--text-muted)',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenModal(response)}
                          sx={{ color: 'var(--primary-500)' }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(response)}
                          sx={{ color: 'var(--error-500)' }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'var(--bg-elevated)',
            border: '1px solid var(--border-light)',
          },
        }}
      >
        <DialogTitle sx={{ color: 'var(--text-primary)' }}>
          {editingResponse ? 'Edit Response' : 'New Canned Response'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Title (English)"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Welcome Greeting"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'var(--bg-primary)',
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Title (Arabic, optional)"
                value={formData.titleAr}
                onChange={(e) => setFormData({ ...formData, titleAr: e.target.value })}
                placeholder="مثال: ترحيب"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'var(--bg-primary)',
                  },
                  '& input': { direction: 'rtl', textAlign: 'right' },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  label="Category"
                >
                  {categories.map(cat => (
                    <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="New Category (optional)"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                InputProps={{
                  endAdornment: newCategory && (
                    <InputAdornment position="end">
                      <Button size="small" onClick={handleAddCategory}>
                        Add
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Content (English)"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Enter the English response... Use {{variable}} for dynamic content"
                required
                multiline
                rows={4}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'var(--bg-primary)',
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Content (Arabic, optional)"
                value={formData.contentAr}
                onChange={(e) => setFormData({ ...formData, contentAr: e.target.value })}
                placeholder="النص بالعربية — اختياري"
                multiline
                rows={4}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'var(--bg-primary)',
                  },
                  '& textarea': { direction: 'rtl', textAlign: 'right' },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Shortcut — English (optional)"
                value={formData.shortcut}
                onChange={(e) => setFormData({ ...formData, shortcut: e.target.value })}
                placeholder="e.g., /greet"
                helperText="Shortcut + space inserts English body"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Keyboard sx={{ color: 'var(--text-muted)' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'var(--bg-primary)',
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Shortcut — Arabic (optional)"
                value={formData.shortcutAr}
                onChange={(e) => setFormData({ ...formData, shortcutAr: e.target.value })}
                placeholder="e.g., /greet_ar"
                helperText="Separate shortcut inserts Arabic body when Arabic text exists"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Keyboard sx={{ color: 'var(--text-muted)' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'var(--bg-primary)',
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Active"
                sx={{ color: 'var(--text-primary)' }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={formData.tags}
                onChange={(e, newValue) => setFormData({ ...formData, tags: newValue as string[] })}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      size="small"
                      {...getTagProps({ index })}
                      key={option}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tags"
                    placeholder="Add tags..."
                    helperText="Press Enter to add a tag"
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseModal} sx={{ color: 'var(--text-secondary)' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!isFormValid}
            sx={{
              backgroundColor: 'var(--primary-600)',
              '&:hover': { backgroundColor: 'var(--primary-700)' },
              '&:disabled': { backgroundColor: 'var(--bg-tertiary)' },
            }}
          >
            {editingResponse ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: 'var(--bg-elevated)',
          },
        }}
      >
        <DialogTitle sx={{ color: 'var(--text-primary)' }}>
          Delete Response
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'var(--text-secondary)' }}>
            Are you sure you want to delete "{responseToDelete?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)} sx={{ color: 'var(--text-secondary)' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CannedResponseManagement;
